import { ClientPerspective, createClient } from "@sanity/client";

import envVariables from "@brexhq/shared/utils/env/envVariables";

const { SANITY_PROJECT_ID } = envVariables;

export const getDatasetFromBranch = (branch: string): string => {
  // takes a branch name like web-123/dataset-im and returns just the dataset name - in this case im
  const parts = branch.split("/");
  const datasetDef = parts[parts.length - 1];
  // if format is wrong return production
  if (!datasetDef.startsWith("dataset-")) {
    return "production";
  }
  return datasetDef.split("dataset-")[1];
};

export const getSanityDataset = () => {
  if (process.env.VERCEL_GIT_COMMIT_REF?.includes("dataset")) {
    const ds = getDatasetFromBranch(process.env.VERCEL_GIT_COMMIT_REF);
    return ds;
  }
  if (process.env.SANITY_DATASET_ID) {
    return process.env.SANITY_DATASET_ID;
  }
  return "production";
};

export const sanityDataset = getSanityDataset();

// This needs to be function so that it can get the correct env variable for read token

type SanityClientOptions = {
  token?: string;
  dataset?: string;
  perspective?: ClientPerspective;
};

/**
 *
 * @param options - customToken is used if you need to invoke a client instance with non-standard permissions
 * @returns SanityClient
 *
 * @NOTE DO NOT use in browser code. You will get token leaks.
 */
export const sanityClient = (
  options?: SanityClientOptions,
  isPreview = false,
) => {
  // only use this if you are 100% sure you want draft content
  if (isPreview) {
    return createClient({
      projectId: SANITY_PROJECT_ID,
      dataset: sanityDataset,
      token: process.env.SANITY_DRAFT_KEY,
      useCdn: false,
      apiVersion: "2021-03-31",
    });
  }

  const defaultToken =
    process.env.BREX_ENV === "development"
      ? process.env.SANITY_DRAFT_KEY
      : process.env.SANITY_KEY;

  const token = options?.token ?? defaultToken;

  return createClient({
    projectId: SANITY_PROJECT_ID,
    dataset: options?.dataset ?? sanityDataset,
    token,
    useCdn: true,
    apiVersion: "2021-03-31",
    perspective: options?.perspective,
  });
};

export const getSanityApiEndpoint = ({ cdn = true }: { cdn?: boolean }) => {
  return `https://${SANITY_PROJECT_ID}.${
    cdn ? "apicdn" : "api"
  }.sanity.io/v1/graphql/${sanityDataset}/default`;
};

/**
 * The following functions are used to fetch content from Sanity
 * in the browser. Add a new function and proxy whenever you
 * want to fetch content from Sanity in the browser in a new place.
 * Fetching content this way avoids token leaks.
 */

export const fetchPartnerDataFromSanityInBrowser = async ({
  isPreview,
  partnerId,
}: {
  isPreview: boolean;
  partnerId: string;
}) => {
  const response = await fetch("/api/fetch-partner-data", {
    method: "POST",
    body: JSON.stringify({
      isPreview,
      partnerId,
    }),
  });

  const result = await response.json();

  if (result.error) {
    console.error(`${response.status}: ${result.error}`);
    return null;
  }

  return result?.content;
};

export const fetchArchiveBannerDataFromSanityInBrowser = async ({
  documentId,
}: {
  documentId: string;
}) => {
  const response = await fetch("/api/fetch-archive-banner-data", {
    method: "POST",
    body: JSON.stringify({
      documentId,
    }),
  });

  const result = await response.json();

  if (result.error) {
    console.error(`${response.status}: ${result.error}`);
    return null;
  }

  return result?.content;
};
